import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Plus, Search, Trash2, X, Loader2, Building2, ChevronLeft, ChevronRight,
} from 'lucide-react';
import {
  b2bCustomerService,
  B2BCustomer,
  B2BCustomerCreate,
} from '../../../services/B2BCustomerService';
import { apiService } from '../../../services/ApiService';
import CustomerDrawer from '../../b2b/CustomerDrawer';

// ── helpers ──────────────────────────────────────────────────────────────────

// Working form shape — delivery_days_override is edited as a string (numeric
// input) and coerced to number|null on submit.
type CustomerForm = Omit<B2BCustomerCreate, 'delivery_days_override'> & { delivery_days_override: string };

const EMPTY_FORM: CustomerForm = {
  name: '',
  alias: '',
  tax_id: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  country: '',
  city: '',
  address: '',
  notes: '',
  delivery_days_override: '',
};

function nullify(obj: CustomerForm): B2BCustomerCreate {
  const { delivery_days_override, ...rest } = obj;
  const base = Object.fromEntries(
    Object.entries(rest).map(([k, v]) => [k, v === '' ? null : v])
  ) as unknown as Omit<B2BCustomerCreate, 'delivery_days_override'>;
  const dd = delivery_days_override.trim();
  return { ...base, delivery_days_override: dd === '' ? null : Number(dd) };
}

// ── types ─────────────────────────────────────────────────────────────────────

interface Notification {
  show: boolean;
  title: string;
  message: string;
  type: 'success' | 'error';
}

// ── component ─────────────────────────────────────────────────────────────────

const CRMCustomersPanel: React.FC = () => {
  const { t } = useTranslation();

  // list state
  const [customers, setCustomers] = useState<B2BCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const LIMIT = 20;

  // modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<B2BCustomer | null>(null);
  const [form, setForm] = useState<CustomerForm>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);

  // delete confirm
  const [deleteTarget, setDeleteTarget] = useState<B2BCustomer | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // drawer
  const [drawerCustomer, setDrawerCustomer] = useState<B2BCustomer | null>(null);
  const [orgAlias, setOrgAlias] = useState('');

  // notification
  const [notification, setNotification] = useState<Notification>({
    show: false, title: '', message: '', type: 'success',
  });

  // ── debounce search ───────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  // ── load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await b2bCustomerService.list({ q: debouncedQ || undefined, page, limit: LIMIT });
      setCustomers(res.data?.customers ?? []);
      setTotal(res.data?.total ?? 0);
    } catch (e: any) {
      console.error('[CRMCustomersPanel] load error:', e?.response?.status, e?.response?.data, e);
      const detail = e?.response?.data?.detail || '';
      const msg = e?.response?.status === 403
        ? detail || t('crm.featureDisabled', 'B2B Customers feature is not enabled for this organisation')
        : detail || t('crm.loadError', 'Failed to load customers');
      notify('Error', msg, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [debouncedQ, page]);

  useEffect(() => { load(); }, [load]);

  // ── fetch org alias once ──────────────────────────────────────────────────
  useEffect(() => {
    apiService.getOrganizationInfo().then(res => {
      setOrgAlias(res.data?.alias || res.data?.organization?.alias || '');
    }).catch(() => {});
  }, []);

  // ── notifications ────────────────────────────────────────────────────────
  const notify = (title: string, message: string, type: 'success' | 'error') => {
    setNotification({ show: true, title, message, type });
    setTimeout(() => setNotification(p => ({ ...p, show: false })), 4000);
  };

  // ── modal helpers ─────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (c: B2BCustomer) => {
    setEditTarget(c);
    setForm({
      name: c.name,
      alias: c.alias ?? '',
      tax_id: c.tax_id ?? '',
      contact_name: c.contact_name ?? '',
      contact_email: c.contact_email ?? '',
      contact_phone: c.contact_phone ?? '',
      country: c.country ?? '',
      city: c.city ?? '',
      address: c.address ?? '',
      notes: c.notes ?? '',
      delivery_days_override: c.delivery_days_override != null ? String(c.delivery_days_override) : '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setIsSaving(true);
    try {
      const payload = nullify(form);
      if (editTarget) {
        await b2bCustomerService.update(editTarget.id, payload);
        notify(t('common.success', 'Success'), t('crm.updated', 'Customer updated'), 'success');
      } else {
        await b2bCustomerService.create(payload);
        notify(t('common.success', 'Success'), t('crm.created', 'Customer created'), 'success');
      }
      setModalOpen(false);
      await load();
    } catch (e: any) {
      notify('Error', e?.response?.data?.detail || t('crm.saveError', 'Failed to save customer'), 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await b2bCustomerService.remove(deleteTarget.id);
      notify(t('common.success', 'Success'), t('crm.deleted', 'Customer removed'), 'success');
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      notify('Error', e?.response?.data?.detail || t('crm.deleteError', 'Failed to delete customer'), 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  // ── pagination ────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t('crm.title', 'B2B Customers')}
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {t('crm.subtitle', 'Manage the businesses your organisation sells to')}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('crm.addCustomer', 'Add Customer')}
        </button>
      </div>

      {/* Notification */}
      {notification.show && (
        <div className={`p-4 rounded-xl border text-sm flex items-start gap-3 ${notification.type === 'success'
          ? 'bg-green-50 border-green-200 text-green-800'
          : 'bg-red-50 border-red-200 text-red-800'}`}>
          <div className="flex-1">
            <p className="font-medium">{notification.title}</p>
            <p className="opacity-90 mt-0.5">{notification.message}</p>
          </div>
          <button onClick={() => setNotification(p => ({ ...p, show: false }))}>
            <X className="w-4 h-4 opacity-60 hover:opacity-100" />
          </button>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder={t('crm.searchPlaceholder', 'Search by company name…')}
          className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300 transition-all"
        />
      </div>

      {/* Table */}
      <div className="border bg-white border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">{t('crm.col.name', 'Company')}</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600 hidden md:table-cell">{t('crm.col.contact', 'Contact')}</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600 hidden lg:table-cell">{t('crm.col.location', 'Location')}</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600 hidden sm:table-cell">{t('crm.col.taxId', 'Tax ID')}</th>
              <th className="w-20 px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                  {t('common.loading', 'Loading…')}
                </td>
              </tr>
            ) : customers.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center">
                  <Building2 className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">
                    {debouncedQ
                      ? t('crm.noResults', 'No customers match your search')
                      : t('crm.empty', 'No customers yet — add your first one')}
                  </p>
                </td>
              </tr>
            ) : (
              customers.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{c.name}</p>
                    {c.alias && <p className="text-xs text-gray-400">{c.alias}</p>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-gray-600">
                    {c.contact_name && <p>{c.contact_name}</p>}
                    {c.contact_email && <p className="text-xs text-gray-400">{c.contact_email}</p>}
                    {c.contact_phone && <p className="text-xs text-gray-400">{c.contact_phone}</p>}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-gray-600">
                    {[c.city, c.country].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-gray-500">
                    {c.tax_id || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => setDrawerCustomer(c)}
                        className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                        title={t('common.manage', 'Manage')}
                      >
                        <Building2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(c)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title={t('common.delete', 'Delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>{t('crm.pagination', '{{from}}–{{to}} of {{total}} customers', {
            from: (page - 1) * LIMIT + 1,
            to: Math.min(page * LIMIT, total),
            total,
          })}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-2">{page} / {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ── Create / Edit Modal ───────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">
                {editTarget ? t('crm.editCustomer', 'Edit Customer') : t('crm.newCustomer', 'New B2B Customer')}
              </h2>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Company name */}
              <Field label={t('crm.field.name', 'Company Name')} required>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Acme Auto Parts Ltd."
                  className={INPUT_CLS}
                />
              </Field>

              <div className="grid grid-cols-2 gap-4">
                <Field label={t('crm.field.alias', 'Short Name / Alias')}>
                  <input type="text" value={form.alias ?? ''} onChange={e => setForm(p => ({ ...p, alias: e.target.value }))} className={INPUT_CLS} />
                </Field>
                <Field label={t('crm.field.taxId', 'Tax ID (VAT / RFC / EIN)')}>
                  <input type="text" value={form.tax_id ?? ''} onChange={e => setForm(p => ({ ...p, tax_id: e.target.value }))} className={INPUT_CLS} />
                </Field>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">{t('crm.section.contact', 'Primary Contact')}</p>
                <div className="space-y-3">
                  <Field label={t('crm.field.contactName', 'Contact Name')}>
                    <input type="text" value={form.contact_name ?? ''} onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))} className={INPUT_CLS} />
                  </Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label={t('crm.field.contactEmail', 'Email')}>
                      <input type="email" value={form.contact_email ?? ''} onChange={e => setForm(p => ({ ...p, contact_email: e.target.value }))} className={INPUT_CLS} />
                    </Field>
                    <Field label={t('crm.field.contactPhone', 'Phone')}>
                      <input type="tel" value={form.contact_phone ?? ''} onChange={e => setForm(p => ({ ...p, contact_phone: e.target.value }))} className={INPUT_CLS} />
                    </Field>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">{t('crm.section.location', 'Location')}</p>
                <div className="grid grid-cols-2 gap-4">
                  <Field label={t('crm.field.city', 'City')}>
                    <input type="text" value={form.city ?? ''} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} className={INPUT_CLS} />
                  </Field>
                  <Field label={t('crm.field.country', 'Country (ISO)')}>
                    <input type="text" maxLength={2} placeholder="US" value={form.country ?? ''} onChange={e => setForm(p => ({ ...p, country: e.target.value.toUpperCase() }))} className={INPUT_CLS} />
                  </Field>
                </div>
                <div className="mt-3">
                  <Field label={t('crm.field.address', 'Address')}>
                    <textarea rows={2} value={form.address ?? ''} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} className={INPUT_CLS + ' resize-none'} />
                  </Field>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <Field label={t('crm.field.deliveryOverride', 'Delivery time override (days)')}>
                  <input type="number" min={0} value={form.delivery_days_override} onChange={e => setForm(p => ({ ...p, delivery_days_override: e.target.value }))} className={INPUT_CLS} placeholder={t('crm.field.deliveryOverridePlaceholder', 'Leave empty to use zone default')} />
                </Field>
              </div>

              <div className="border-t border-gray-100 pt-4">
                <Field label={t('crm.field.notes', 'Internal Notes')}>
                  <textarea rows={3} value={form.notes ?? ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className={INPUT_CLS + ' resize-none'} placeholder={t('crm.field.notesPlaceholder', 'Only visible to your team…')} />
                </Field>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !form.name.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editTarget ? t('common.save', 'Save') : t('crm.addCustomer', 'Add Customer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ──────────────────────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-xl">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-base font-semibold text-gray-900">{t('crm.confirmDelete', 'Remove customer?')}</h2>
            </div>
            <p className="text-sm text-gray-600">
              <Trans
                i18nKey="crm.confirmDeleteMsg"
                defaults="This will remove <strong>{{name}}</strong> from your customer list. The record is kept for audit purposes."
                values={{ name: deleteTarget.name }}
                components={{ strong: <strong /> }}
              />
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors">
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex items-center gap-2 px-5 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-60 transition-colors"
              >
                {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('common.remove', 'Remove')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Customer Detail Drawer ────────────────────────────────────────── */}
      <CustomerDrawer
        customer={drawerCustomer}
        orgAlias={orgAlias}
        onClose={() => setDrawerCustomer(null)}
        onUpdated={updated => {
          setCustomers(prev => prev.map(c => (c.id === updated.id ? updated : c)));
          setDrawerCustomer(updated);
        }}
      />
    </div>
  );
};

// ── tiny helper ───────────────────────────────────────────────────────────────

const INPUT_CLS = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-300 transition-all';

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

export default CRMCustomersPanel;
